import { useQueries } from '@tanstack/react-query';
import _ from 'lodash';
import { queryKeys } from './queryKeys';
import { API } from '../api';
import { UserProgramInfo } from '../pages/new-designs/tabs/Program/utils/programTypes';

interface CachedGroup {
  grpId: string;
  name?: string;
  type?: string;
  status?: string;
}

interface Args {
  groupIds: string[];
  roleLabelsById: Record<string, string[]>;
  cachedGroups: CachedGroup[];
  orgId: string;
  location: string;
}

interface ProgramQueryArgs {
  id: string;
  roles: string[];
  cached?: CachedGroup;
  orgId: string;
  location: string;
}

const fetchProgram = async ({ id, roles, cached, orgId, location }: ProgramQueryArgs): Promise<UserProgramInfo> => {
  let info: UserProgramInfo = {
    id,
    name: cached?.name || 'Program',
    type: _.startCase(cached?.type || 'Program'),
    roles,
    status: cached?.status,
    loading: false
  };

  try {
    const response = await API.getGroupDetails(id, undefined, true);
    const details = response?.data;
    info = {
      ...info,
      name: details?.name || info.name,
      type: _.startCase(details?.goals?.programType || details?.type || info.type),
      progress: details?.progress ?? details?.programProgress ?? info.progress,
      status: details?.status ?? info.status
    };
  } catch {
    /* fallback to base/cached */
  }

  const hasMenteeRole = roles.some((r) => r.toLowerCase().includes('mentee'));
  const hasMentorRole = roles.some((r) => r.toLowerCase().includes('mentor'));

  const enrichers: Promise<void>[] = [];

  if (hasMenteeRole) {
    enrichers.push(
      API.getMenteeProgress({ orgId, grpId: id })
        .then((resp) => {
          const data = resp?.data?.data || resp?.data;
          info = {
            ...info,
            progress:
              data?.progress ??
              data?.progressPercentage ??
              data?.progress_percent ??
              data?.progressPerc ??
              data?.programProgress ??
              info.progress,
            etaText: data?.etaText ?? info.etaText,
            objectivesPending: data?.objectivesPending ?? data?.objectivePending ?? data?.objectivesRemaining
          };
        })
        .catch(() => undefined)
    );
  }

  if (hasMentorRole) {
    enrichers.push(
      API.getMentorsPeersHomePage(orgId, location, id)
        .then((resp) => {
          const data = resp?.data;
          info = {
            ...info,
            menteesEngaged: data?.connections ?? data?.menteesEngaged ?? data?.menteesCount ?? info.menteesEngaged,
            messagesSent: data?.messagesSent ?? data?.messages ?? info.messagesSent,
            meetingsScheduled: data?.meetingsScheduled ?? data?.meetings ?? info.meetingsScheduled
          };
        })
        .catch(() => undefined)
    );
  }

  await Promise.all(enrichers);
  return info;
};

const useUserPrograms = ({ groupIds, roleLabelsById, cachedGroups, orgId, location }: Args) => {
  const uniqueIds = Array.from(new Set(groupIds));
  const cacheById = new Map(cachedGroups.map((g) => [g.grpId, g]));

  const queries = useQueries({
    queries: uniqueIds.map((id) => {
      const roles = roleLabelsById[id] || ['User'];
      const cached = cacheById.get(id);
      return {
        queryKey: [queryKeys.GET_USER_PROGRAM, id, roles.join('|')],
        queryFn: () => fetchProgram({ id, roles, cached, orgId, location }),
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        enabled: Boolean(id && orgId)
      };
    })
  });

  const items = uniqueIds.map((id, idx): UserProgramInfo => {
    const q = queries[idx];
    const roles = roleLabelsById[id] || ['User'];
    const cached = cacheById.get(id);
    if (q?.data) return q.data;
    const placeholder: UserProgramInfo = {
      id,
      name: cached?.name || 'Program',
      type: _.startCase(cached?.type || 'Program'),
      roles,
      status: cached?.status,
      loading: q?.isPending ?? true
    };
    return placeholder;
  });

  const isLoading = queries.some((q) => q.isPending);
  const allSettled = queries.every((q) => q.isFetched || !q.isPending);

  return { items, isLoading, allSettled };
};

export default useUserPrograms;
